// What a failed GitHub write means to the user. GitHub answers "Resource not
// accessible by integration" when the changelist GitHub App is not installed
// on the repository (the user's App token then reaches only what the App
// can), or when its installation lacks a permission the write needs.
import { HttpError } from './cache';
import { GraphQLFailure, isSamlError } from './graphqlError';
import { RateLimitError } from './rateLimit';

const INTEGRATION = /resource not accessible by integration/i;

/** Where the write was going and how to install the App. */
export interface WriteContext {
  owner: string;
  repo: string;
  /** The App's slug from `/api/config` (null: no install link). */
  appSlug: string | null | undefined;
  /** GitHub's web origin (`/api/config` `github_web_url`). */
  webUrl?: string | null;
}

export interface WriteError {
  text: string;
  /** The App's installation page, when the failure is the App's access. */
  link: string | null;
  /** What the link says ("Install the Changeling App on <owner>"). */
  linkText?: string;
}

/**
 * True when the changelist server refused its own data (review marks, held
 * comments, attention overrides) because no installation of the App covers
 * the repository: 403 `not_installed`. GitHub still shows the repository.
 */
export function isNotInstalled(e: unknown): boolean {
  return e instanceof HttpError && e.status === 403 && (e.body as { error?: unknown } | null)?.error === 'not_installed';
}

/** `{web}/apps/{slug}/installations/new`, or null without a slug. */
export function installUrl(appSlug: string | null | undefined, webUrl?: string | null): string | null {
  if (!appSlug) return null;
  return `${(webUrl || 'https://github.com').replace(/\/+$/, '')}/apps/${encodeURIComponent(appSlug)}/installations/new`;
}

/**
 * True when GitHub refused a write because of the App's access: its
 * "Resource not accessible by integration" message from REST or GraphQL,
 * or a FORBIDDEN error from a GraphQL mutation (other than SAML
 * enforcement). The bridge's own denials (`bridge_denied`) and rate limits
 * are not.
 */
export function isIntegrationError(e: unknown): boolean {
  if (e instanceof RateLimitError) return false;
  if (e instanceof GraphQLFailure)
    return e.errors.some((x) => !isSamlError(x) && (INTEGRATION.test(x.message) || x.type === 'FORBIDDEN'));
  if (e instanceof HttpError) {
    const body = e.body as { error?: string; message?: string; detail?: string } | null;
    if (body && typeof body === 'object' && body.error === 'bridge_denied') return false;
    // The changelist server's own GitHub calls (publishing drafts) report GitHub's refusal in `detail`.
    if (body && typeof body === 'object' && body.error === 'upstream_error') return INTEGRATION.test(String(body.detail ?? ''));
    return e.status === 403 && INTEGRATION.test(typeof body === 'object' && body ? String(body.message ?? '') : String(body ?? e.message));
  }
  return e instanceof Error && INTEGRATION.test(e.message);
}

/**
 * The sentence shown for an integration failure (the link follows it). The
 * App may be installed on the user's own account but not on the account
 * that owns the repository; a public repository then reads fine and
 * refuses every write.
 */
export function cannotWriteText(ctx: Pick<WriteContext, 'owner' | 'repo'>): string {
  return (
    `Changeling can't write to ${ctx.owner}/${ctx.repo}: the GitHub App isn't installed on ${ctx.owner} ` +
    `(the account that owns ${ctx.owner}/${ctx.repo}), or its installation lacks Pull requests: write.`
  );
}

/** The install link's text. */
export function installLinkText(owner: string): string {
  return `Install the Changeling App on ${owner}`;
}

/** Explain a failed write; null when it is not the App's access (show GitHub's message). */
export function describeWriteError(e: unknown, ctx: WriteContext): WriteError | null {
  if (!isIntegrationError(e)) return null;
  const link = installUrl(ctx.appSlug, ctx.webUrl);
  return { text: cannotWriteText(ctx), link, linkText: link ? installLinkText(ctx.owner) : undefined };
}
